'use client';

import { Typography, ImageLabel, ServiceLogo } from '@wishket/design-system';
import Link, { LinkProps } from 'next/link';
import { PropsWithChildren, useState } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

type ServiceLogoNames = 'wishket_symbol' | 'yozmIT_symbol' | 'jobs_symbol';

interface ServiceItemProps {
  name: string;
  href: string;
  icon: string;
  disabled?: boolean;
}

export const ServiceLinkItem = ({
  name,
  href,
  icon,
  disabled = true,
}: ServiceItemProps) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);

  return (
    <Link
      className={twMerge(
        'box-border flex cursor-pointer items-center gap-1 no-underline desktop:hover:text-w-gray-900',
        disabled ? 'text-w-gray-400' : 'text-w-gray-900'
      )}
      href={href}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <ServiceLogo
        name={icon as ServiceLogoNames}
        className="size-[18px]"
        variant={!isHovered && disabled ? 'disabled' : 'default'}
      />
      <Typography variant="caption1">{name}</Typography>
    </Link>
  );
};

interface GlobalNavigationLinkItemProps extends PropsWithChildren<LinkProps> {
  isNew?: boolean;
  hasAiLabel?: boolean;
}

export const GlobalNavigationLinkItem = ({
  children,
  href,
  isNew,
  hasAiLabel,
}: GlobalNavigationLinkItemProps) => {
  return (
    <Link href={href} className="group px-3 py-5 ">
      <Typography
        variant="body2"
        className="flex items-center gap-1 font-medium text-w-gray-900 desktop:group-hover:text-y-primary"
      >
        {children}
        {hasAiLabel && <ImageLabel type="ai" />}
        {isNew && <ImageLabel type="new_circle" />}
      </Typography>
    </Link>
  );
};

interface SubNavigationLinkItemProps extends PropsWithChildren<LinkProps> {
  isNew?: boolean;
  hasAiLabel?: boolean;
  isActive?: boolean;
  isMain?: boolean;
}

export const SubNavigationLinkItem = ({
  children,
  href,
  isNew,
  hasAiLabel,
  isActive,
  isMain,
}: SubNavigationLinkItemProps) => {
  return (
    <Link
      href={href}
      className={twJoin('group relative', isActive && 'font-medium')}
    >
      <Typography
        variant="body2"
        className={twMerge(
          'flex items-center gap-1 py-3 text-w-gray-700',
          isMain && 'font-medium text-w-gray-900'
        )}
      >
        {children}
        {hasAiLabel && <ImageLabel type="ai" />}
        {isNew && <ImageLabel type="new_circle" />}
      </Typography>
      <div
        className={twJoin(
          'absolute bottom-0 left-0 h-0.5 w-full rounded-t-sm',
          isActive ? 'bg-primary' : 'desktop:group-hover:bg-w-gray-200'
        )}
      />
    </Link>
  );
};
